import { useAPI } from "@/contexts/APIContext";
import { Link } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";

interface ProfileHeaderProps {
  profile: User.Profile & { isBlockedByUser?: boolean };
  isOwnProfile: boolean;
  onToggleMoreActions: () => void;
  showMoreActions: boolean;
  onBlockUser?: () => void;
  onUnblockUser?: () => void;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long" });
};

export default function ProfileHeader({
  profile,
  isOwnProfile,
  onToggleMoreActions,
  showMoreActions,
  onBlockUser,
  onUnblockUser,
}: ProfileHeaderProps) {
  const { user } = useAPI();

  return (
    <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-10">
      {/* Profile Image */}
      <div className="relative">
        <img
          src={profile.profileImageUrl}
          alt={profile.name || "User"}
          className="w-36 h-36 rounded-full object-cover"
        />
      </div>

      {/* Profile Info */}
      <div className="flex-1 text-center md:text-left">
        <h1 className="text-2xl font-semibold mb-2">{profile.name}</h1>
        <p className="text-gray-600 mb-3">{profile.description}</p>
        <p className="text-sm text-gray-500">
          Member since {formatDate(profile.createdAt?.toISOString() || new Date().toISOString())}
        </p>

        {/* Action Buttons */}
        <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-3">
          {isOwnProfile ? (
            <Link
              to="/settings"
              className="bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-full transition"
            >
              Settings
            </Link>
          ) : (
            <>
              <Link
                to={`/chat/${profile.userId}`}
                className="bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-full transition"
              >
                Message
              </Link>
              {user && (
                <div className="relative">
                  <button
                    onClick={onToggleMoreActions}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-full transition"
                  >
                    More
                  </button>
                  {showMoreActions && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        {profile.isBlockedByUser ? (
                          <button
                            onClick={onUnblockUser}
                            className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                          >
                            Unblock User
                          </button>
                        ) : (
                          <button
                            onClick={onBlockUser}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            Block User
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
